/**
 * ARK testimonials: infinite loop carousel, center-active, drag-to-slide.
 * Root: [data-ark-testimonials]
 */
(function () {
  const ROOT_SELECTOR = '[data-ark-testimonials]';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {HTMLElement} el
   * @returns {number}
   */
  function parseTranslateX(el) {
    const t = window.getComputedStyle(el).transform;
    if (!t || t === 'none') return 0;
    const m2 = t.match(/matrix\(([^)]+)\)/);
    if (m2) {
      const p = m2[1].split(',').map(Number);
      return p[4] || 0;
    }
    const m3 = t.match(/matrix3d\(([^)]+)\)/);
    if (m3) {
      const p = m3[1].split(',').map(Number);
      return p[12] || 0;
    }
    return 0;
  }

  class TestimonialsCarousel {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-ark-testimonials-viewport]');
      this.track = root.querySelector('[data-ark-testimonials-track]');
      this.btnPrev = root.querySelector('[data-ark-testimonials-prev]');
      this.btnNext = root.querySelector('[data-ark-testimonials-next]');

      this.cards = [];
      /** @type {number} original testimonial count */
      this.logicalN = 0;
      this.infinite = false;
      this.index = 0;

      /** @type {number | null} */
      this.pointerId = null;
      this.dragStartX = 0;
      this.dragStartTranslate = 0;
      /** @type {number} Active index when drag started — used for swipe commit + one-slide cap */
      this.dragStartIndex = 0;
      this.isDragging = false;

      this._onResize = this._onResize.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._onTransitionEnd = this._onTransitionEnd.bind(this);

      if (!this.viewport || !this.track) return;

      const logicalFromAttr = parseInt(this.track.getAttribute('data-ark-logical-count') || '0', 10);
      const nodeList = Array.from(this.track.querySelectorAll('[data-ark-testimonials-card]'));

      /** @type {HTMLElement[]} */
      let originals;
      if (logicalFromAttr > 0) {
        this.logicalN = logicalFromAttr;
        originals = nodeList.slice(0, this.logicalN);
      } else {
        originals = nodeList.slice();
        this.logicalN = originals.length;
      }

      if (this.logicalN === 0) return;

      if (this.logicalN >= 2) {
        this.infinite = true;
        const currentTotal = this.track.querySelectorAll('[data-ark-testimonials-card]').length;
        if (currentTotal === this.logicalN) {
          originals.forEach(function (card) {
            this.track.appendChild(/** @type {HTMLElement} */ (card.cloneNode(true)));
            this.track.appendChild(/** @type {HTMLElement} */ (card.cloneNode(true)));
          }, this);
        }
        this.cards = Array.from(this.track.querySelectorAll('[data-ark-testimonials-card]'));
        this.index = this.logicalN;
      } else {
        this.cards = originals;
        this.index = 0;
      }

      this.viewport.addEventListener('pointerdown', this._onPointerDown);
      this.viewport.addEventListener('pointermove', this._onPointerMove);
      this.viewport.addEventListener('pointerup', this._onPointerUp);
      this.viewport.addEventListener('pointercancel', this._onPointerUp);
      this.viewport.addEventListener('dragstart', this._onDragStart);

      this.btnPrev?.addEventListener('click', () => this.go(-1));
      this.btnNext?.addEventListener('click', () => this.go(1));

      this.track.addEventListener('transitionend', this._onTransitionEnd);

      window.addEventListener('resize', this._onResize);

      this.update(false);
    }

    /** Normalize physical index into middle clone set (infinite only). */
    normalizeIndex() {
      if (!this.infinite || this.logicalN < 2) return false;
      let changed = false;
      while (this.index >= 2 * this.logicalN) {
        this.index -= this.logicalN;
        changed = true;
      }
      while (this.index < this.logicalN) {
        this.index += this.logicalN;
        changed = true;
      }
      return changed;
    }

    getMetrics() {
      const card = this.cards[0];
      if (!card) return { cardWidth: 0, gap: 0, viewportWidth: 0, viewportPadLeft: 0, viewportPadRight: 0 };
      const trackStyle = window.getComputedStyle(this.track);
      const vpStyle = window.getComputedStyle(this.viewport);
      const gapVal = trackStyle.gap || trackStyle.columnGap || '0';
      const gap = parseFloat(gapVal) || 0;
      const cardWidth = card.offsetWidth;
      const viewportWidth = this.viewport.clientWidth;
      const viewportPadLeft = parseFloat(vpStyle.paddingLeft) || 0;
      const viewportPadRight = parseFloat(vpStyle.paddingRight) || 0;
      return { cardWidth, gap, viewportWidth, viewportPadLeft, viewportPadRight };
    }

    /**
     * X translate so card `idx` is centered within the padded content area (not full clientWidth —
     * track sits inside viewport padding).
     */
    translateForIndex(idx) {
      const { cardWidth, gap, viewportWidth, viewportPadLeft } = this.getMetrics();
      const slideUnit = cardWidth + gap;
      const slideOffset = idx * slideUnit;
      const centerAnchor = viewportWidth / 2 - cardWidth / 2 - viewportPadLeft;
      return centerAnchor - slideOffset;
    }

    /** @param {number} translate */
    indexFromTranslate(translate) {
      const { cardWidth, gap, viewportWidth, viewportPadLeft } = this.getMetrics();
      const slideUnit = cardWidth + gap;
      if (slideUnit <= 0) return this.index;
      const centerAnchor = viewportWidth / 2 - cardWidth / 2 - viewportPadLeft;
      const raw = (centerAnchor - translate) / slideUnit;
      let idx = Math.round(raw);
      idx = Math.max(0, Math.min(this.cards.length - 1, idx));
      return idx;
    }

    /** @param {TransitionEvent} e */
    _onTransitionEnd(e) {
      if (e.target !== this.track || e.propertyName !== 'transform') return;
      const before = this.index;
      const changed = this.normalizeIndex();
      if (changed && this.index !== before) {
        this.update(false);
      }
    }

    /** @param {boolean} animate */
    update(animate) {
      if (!this.infinite) {
        const t = this.translateForIndex(this.index);
        const reduced = prefersReducedMotion();
        this.track.style.transition =
          animate && !reduced ? 'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
        this.track.style.transform = 'translate3d(' + t + 'px, 0, 0)';

        this.cards.forEach((card, i) => {
          const active = i === this.index;
          card.classList.toggle('is-active', active);
          card.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        if (this.btnPrev) this.btnPrev.disabled = this.index <= 0;
        if (this.btnNext) this.btnNext.disabled = this.index >= this.cards.length - 1;
        return;
      }

      if (!animate) {
        this.normalizeIndex();
      }

      const t = this.translateForIndex(this.index);
      const reduced = prefersReducedMotion();
      this.track.style.transition =
        animate && !reduced ? 'transform 0.38s cubic-bezier(0.22, 1, 0.36, 1)' : 'none';
      this.track.style.transform = 'translate3d(' + t + 'px, 0, 0)';

      if (this.infinite && this.logicalN >= 2 && (reduced || !animate)) {
        if (this.normalizeIndex()) {
          const tSnap = this.translateForIndex(this.index);
          this.track.style.transition = 'none';
          this.track.style.transform = 'translate3d(' + tSnap + 'px, 0, 0)';
        }
      }

      this.cards.forEach((card, i) => {
        const active = i === this.index;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (this.logicalN >= 2) {
        if (this.btnPrev) this.btnPrev.disabled = false;
        if (this.btnNext) this.btnNext.disabled = false;
      }
    }

    /** @param {number} delta */
    go(delta) {
      if (this.logicalN < 1) return;

      if (this.infinite && this.logicalN >= 2) {
        this.index += delta;
        this.update(true);
        return;
      }

      const next = Math.max(0, Math.min(this.cards.length - 1, this.index + delta));
      if (next === this.index) return;
      this.index = next;
      this.update(true);
    }

    _onResize() {
      this.update(false);
    }

    /** @param {DragEvent} e */
    _onDragStart(e) {
      e.preventDefault();
    }

    /** @param {PointerEvent} e */
    _onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (this.logicalN <= 1) return;
      if (/** @type {HTMLElement} */ (e.target).closest('button,a')) return;

      this.pointerId = e.pointerId;
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartTranslate = this.translateForIndex(this.index);
      this.dragStartIndex = this.index;
      e.preventDefault();
      try {
        this.viewport.setPointerCapture(e.pointerId);
      } catch (_) {}
      this.track.style.transition = 'none';
    }

    /** @param {PointerEvent} e */
    _onPointerMove(e) {
      if (!this.isDragging || e.pointerId !== this.pointerId) return;
      e.preventDefault();
      const dx = e.clientX - this.dragStartX;
      const t = this.dragStartTranslate + dx;
      this.track.style.transform = 'translate3d(' + t + 'px, 0, 0)';
    }

    /** @param {PointerEvent} e */
    _onPointerUp(e) {
      if (e.pointerId !== this.pointerId || !this.isDragging) return;

      const releasedPointerId = e.pointerId;
      this.isDragging = false;
      this.pointerId = null;

      try {
        this.viewport.releasePointerCapture(releasedPointerId);
      } catch (_) {}

      const metrics = this.getMetrics();
      const currentTranslate = parseTranslateX(this.track);
      const slideSize = metrics.cardWidth + metrics.gap;

      /** Nearest slide index from pointer release position */
      let target = slideSize > 0 ? this.indexFromTranslate(currentTranslate) : this.dragStartIndex;

      /** Lock to at most one slide advance per drag gesture */
      let deltaSlide = target - this.dragStartIndex;
      deltaSlide =
        deltaSlide !== 0 ? Math.sign(deltaSlide) * Math.min(Math.abs(deltaSlide), 1) : 0;

      /** Short horizontal drag still commits (~5.5% inner width, clamped) */
      if (slideSize > 0 && deltaSlide === 0) {
        const dragDx = currentTranslate - this.dragStartTranslate;
        const innerW = Math.max(
          metrics.viewportWidth - metrics.viewportPadLeft - metrics.viewportPadRight,
          60
        );
        const thresh = Math.min(Math.max(innerW * 0.055, 18), 52);
        if (dragDx <= -thresh) deltaSlide = 1;
        else if (dragDx >= thresh) deltaSlide = -1;
      }

      target = this.dragStartIndex + deltaSlide;
      target = Math.max(0, Math.min(this.cards.length - 1, target));

      this.index = target;
      this.update(true);
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (node) {
      new TestimonialsCarousel(/** @type {HTMLElement} */ (node));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
